import { Pressable, StyleSheet, Text } from 'react-native';
import { Icon } from '../index';
import { webTapFloor } from '../theme';

import type { PressGesture } from '../primitives/Chip';
import type { useTheme } from './theme';

// ⭐ The card's position marker, in ONE place: a glyph naming the axis, "N/M"
// in tabular figures, and a tap that steps forward. The superset cue (which
// exercise of the group) and the week cue (which week of the cycle) are the
// same control over two different axes; writing the second one out again
// would have been the copy §CONSOLIDAMENTO forbids, and the two would have
// drifted in box, gap and figure style the first time either was touched.
// ⛔ `onPress` absent ⇒ the cue is a READOUT: still announced, no longer a
// button, so a screen reader is never told to tap something that goes nowhere.
export function PositionCue({
  theme,
  glyph,
  position,
  total,
  prefix = '',
  accessibilityLabel,
  accessibilityHint,
  onPress,
  onPrev,
  prevLabel,
  gesture,
  hitSlop,
}: {
  theme: ReturnType<typeof useTheme>;
  glyph: 'repeat' | 'calendar';
  position: number;
  total: number;
  /** Prepended to the visible count only ("W2/4"), never to the spoken label,
   *  which says the axis in words. */
  prefix?: string;
  accessibilityLabel: string;
  accessibilityHint?: string;
  onPress?: () => void;
  /** The step BACKWARD, with no glyph of its own: a named action so a screen
   *  reader can reach it. Absent on the superset cue, where the members are
   *  two or three and wrapping forward costs nothing. */
  onPrev?: () => void;
  /** Names `onPrev` for the screen reader. Required with it, ignored without. */
  prevLabel?: string;
  /** The hold and hover: since #308 they show the cue's hint, like every
   *  card control (useTransientTip.holdProps). */
  gesture?: PressGesture;
  hitSlop: { top: number; bottom: number; left: number; right: number };
}) {
  const styles = makeStyles(theme);
  // The step back and the hold's "Show hint" are both named actions; the
  // bundle's handler gets every action that is not the step back.
  const back = onPrev && prevLabel;
  const actions = [
    ...(back ? [{ name: 'decrement', label: prevLabel }] : []),
    ...(gesture?.accessibilityActions ?? []),
  ];
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      {...gesture}
      accessibilityActions={actions.length > 0 ? actions : undefined}
      onAccessibilityAction={(e) => {
        if (back && e.nativeEvent.actionName === 'decrement') onPrev();
        else gesture?.onAccessibilityAction?.(e);
      }}
      disabled={!onPress}
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ pressed }) => [
        styles.cue,
        pressed && onPress ? styles.cuePressed : null,
      ]}
    >
      <Icon
        name={glyph}
        size={theme.typography.sizes.md}
        color={theme.palette.ink}
      />
      <Text style={styles.cueCount}>
        {prefix}
        {position}/{total}
      </Text>
    </Pressable>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Same 36-box treatment as the IconButtons beside it (surface2 +
    // borderStrong, DS-9) so the card reads as one control family; the glyph
    // is the axis marker (a SHAPE cue, never colour-only: `repeat` =
    // superset, `calendar` = week); the count is neutral ink to dodge
    // brand-on-fill contrast debt (DS-11).
    cue: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
      // RNW ignores hitSlop, so on the web the box itself reaches 44.
      minHeight: webTapFloor ?? 36,
      paddingHorizontal: s.s2,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.borderStrong,
      backgroundColor: p.surface2,
    },
    cuePressed: { backgroundColor: p.surface },
    cueCount: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
  });
}
